/**
 * Sections API - Update Section
 *
 * PUT /v1/schools/:schoolId/sections/:sectionId
 *
 * Mirrors apps/backend/src/api/schools/v1/use-cases/(sections)/sections/update-single/schema.ts.
 * Presentation fields (designTheme, banner/avatar) anchor on the lead section
 * per comms v2 U13 (Section Presentation Parity).
 */

import { z } from 'zod'
import { GroupDesignThemeSchema } from '../groups/common'
// Source of truth for the 7 section-space feature toggles (PR-B U2). Imported
// from the file (not the spaces barrel) to keep the dependency narrow — the
// schema only depends on zod, so there is no circular-dep risk.
import { UpdateSpaceSectionSettingsSchema } from '../spaces/settings-update'
import type { UpdateSpaceSectionSettings } from '../spaces/settings-update'
import { AppVisibilitySchema } from '../spaces/common'

/**
 * Stream feed grouping for the section's lead UserGroup. Mirrors the backend
 * `StreamFeedGroup` Prisma enum (apps/backend/prisma/schema/groups.prisma).
 */
export const SectionStreamFeedGroupSchema = z.enum(['TIMELINE', 'GROUP', 'NOTIFICATION', 'APP_MESSAGE'])

export const UpdateSectionRequestSchema = z
  .object({
    // Auto-publishing controls
    autoPublishEnabled: z.boolean().optional(),
    autoPublishNews: z.boolean().optional(),
    autoPublishAnnouncements: z.boolean().optional(),
    autoPublishEvents: z.boolean().optional(),

    // Notification settings
    defaultNotificationMode: z.string().nullable().optional(),

    // Presentation fields — anchored on the lead section (comms v2 U13).
    // designTheme is validated against the canonical GroupDesignTheme shape so
    // primitives, arrays, and malformed objects are rejected at the API boundary.
    designTheme: GroupDesignThemeSchema.nullable().optional(),
    bannerBackgroundColor: z.string().nullable().optional(),
    bannerLogoUrl: z.string().nullable().optional(),
    bannerImageUrl: z.string().nullable().optional(),
    avatarImageUrl: z.string().nullable().optional(),

    // Bucket A fields (section-canonical write-path cutover) — mirror the backend
    // update-single schema. Mobile's leader visibility toggle writes `showInApp`.
    showInApp: z.boolean().optional(),
    // 3-state visibility override. Takes precedence over showInApp when both are
    // present; the backend derives app_visibility from showInApp when absent.
    appVisibility: AppVisibilitySchema.optional(),
    betaTestersOnly: z.boolean().optional(),
    displayName: z.string().nullable().optional(),
    // NOTE: `descriptionOverride` is deliberately NOT writable. A section's
    // description is synced from the SIS and cannot be pushed back to Blackbaud,
    // so persisting a local override would silently diverge from the SIS. The
    // response schema still echoes the (legacy) stored value.
    emailSlug: z.string().nullable().optional(),
    streamFeedGroup: SectionStreamFeedGroupSchema.optional()
  })
  // Section-level space feature toggles (canonical home on the Section row; PR-B
  // U1). The 7 editable toggles (chat/mediaLibrary/memberList/memberPosting/
  // comments/reactions/sharing) are reused from the shared
  // UpdateSpaceSectionSettingsSchema — the source of truth — which deliberately
  // EXCLUDES contactEnabled / defaultShowInTimeline / defaultNotifyLevel.
  .merge(UpdateSpaceSectionSettingsSchema)

export type UpdateSectionRequest = z.infer<typeof UpdateSectionRequestSchema>

export const UpdatedSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  offeringType: z.string(),

  autoPublishEnabled: z.boolean(),
  autoPublishNews: z.boolean(),
  autoPublishAnnouncements: z.boolean(),
  autoPublishEvents: z.boolean(),
  defaultNotificationMode: z.string().nullable(),

  designTheme: GroupDesignThemeSchema.nullable(),
  bannerBackgroundColor: z.string().nullable(),
  bannerLogoUrl: z.string().nullable(),
  bannerImageUrl: z.string().nullable(),
  avatarImageUrl: z.string().nullable(),

  // Bucket A echo — mirrors the backend update-single response.
  showInApp: z.boolean(),
  betaTestersOnly: z.boolean(),
  displayName: z.string().nullable(),
  descriptionOverride: z.string().nullable(),
  emailSlug: z.string().nullable(),
  streamFeedGroup: SectionStreamFeedGroupSchema,

  // Section-level space feature toggles (echo of persisted Section values; PR-B U1).
  // Required in the response — the endpoint always echoes the current toggle state.
  // Mirrors the 7 editable toggles in UpdateSpaceSectionSettingsSchema.
  chatEnabled: z.boolean().describe('Group chat enabled'),
  mediaLibraryEnabled: z.boolean().describe('Media library enabled'),
  memberListEnabled: z.boolean().describe('Member list visible'),
  memberPostingEnabled: z.boolean().describe('Member posting allowed'),
  // Member media contribute toggle (plan 2026-07-22-002 U5) — echoed on the
  // response. `.optional()` for deploy skew: a pre-promotion backend that does
  // not yet return the column must still parse for a client that ships first.
  memberMediaEnabled: z.boolean().optional().describe('Member media uploads into existing albums allowed'),
  commentsEnabled: z.boolean().describe('Comments allowed'),
  reactionsEnabled: z.boolean().describe('Reactions allowed'),
  sharingEnabled: z.boolean().describe('Sharing allowed'),

  updatedAt: z.string()
})

export type UpdatedSection = z.infer<typeof UpdatedSectionSchema>

export const UpdateSectionResponseSchema = z.object({
  message: z.string(),
  data: UpdatedSectionSchema
})

export type UpdateSectionResponse = z.infer<typeof UpdateSectionResponseSchema>

// Re-export the section-space feature-toggle request type from this barrel so a
// SECTIONS endpoint consumer imports it from `api/sections` rather than reaching
// cross-barrel into `api/spaces` (the schema is already merged into
// UpdateSectionRequestSchema above). `UpdateSectionSettings` is the
// section-named alias; both name the same shape. Type-only re-export — no
// runtime dependency, no circular-dep risk.
export type { UpdateSpaceSectionSettings }
export type UpdateSectionSettings = UpdateSpaceSectionSettings
